﻿class ShippingCalculator extends HTMLElement {
  connectedCallback() {
    this.moneyFormat = this.dataset.moneyFormat || '';
    this.country = this.dataset.country || 'Brazil';
    this.labels = {
      calculate: this.dataset.calculateLabel || 'CALCULAR',
      calculating: this.dataset.calculatingLabel || 'CALCULANDO...',
      invalid: this.dataset.invalidLabel || 'Digite um CEP válido, com 8 dígitos.',
      empty: this.dataset.emptyLabel || 'Não encontramos entrega para este CEP.',
      error: this.dataset.errorLabel || 'Não foi possível calcular agora. Tente novamente.',
      days: this.dataset.daysLabel || 'dias úteis',
      day: this.dataset.dayLabel || 'dia útil'
    };

    this.selectable = this.dataset.selectable === 'true';
    this.rates = [];

    this.input = this.querySelector('[data-shipping-zip]');
    this.button = this.querySelector('[data-shipping-submit]');
    this.clearButton = this.querySelector('[data-shipping-clear]');
    this.resultsEl = this.querySelector('[data-shipping-results]');
    this.messageEl = this.querySelector('[data-shipping-message]');

    this.input?.addEventListener('input', () => this.maskZip());

    this.clearButton?.addEventListener('click', (event) => {
      event.preventDefault();
      this.clear();
    });

    this.input?.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      event.preventDefault();
      this.calculate();
    });

    this.button?.addEventListener('click', (event) => {
      event.preventDefault();
      this.calculate();
    });

    if (this.selectable) this.restoreQuote();
  }

  get storageKey() {
    return 'ls-shipping-quote';
  }

  readCache() {
    try {
      return JSON.parse(sessionStorage.getItem(this.storageKey) || 'null');
    } catch (error) {
      return null;
    }
  }

  writeCache(value) {
    try {
      if (value) sessionStorage.setItem(this.storageKey, JSON.stringify(value));
      else sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      /* sessionStorage unavailable */
    }
  }

  toggleClear(show) {
    this.clearButton?.classList.toggle('display-none', !show);
  }

  async clear() {
    if (this.input) this.input.value = '';
    this.toggleClear(false);
    this.showMessage('');
    this.writeCache(null);

    if (!this.selectable) {
      if (this.resultsEl) this.resultsEl.innerHTML = '';
      return;
    }

    await this.writeQuoteAndRender('');
  }

  restoreQuote() {
    const cache = this.readCache();

    if (!this.hasAttribute('data-quoted-price')) {
      this.writeCache(null);
      return;
    }

    if (!cache || !cache.rates?.length) return;

    if (this.input) {
      this.input.value = cache.zip || '';
      this.maskZip();
    }

    this.renderRates(cache.rates, cache.selectedIndex);
    this.toggleClear(true);
  }

  maskZip() {
    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  currentVariantId() {
    const section = this.closest('.product-page') || document;
    const field = section.querySelector('form[action*="/cart/add"] [name="id"]');
    return field?.value || this.dataset.variantId || null;
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    this.button.textContent = busy ? this.labels.calculating : this.labels.calculate;
  }

  showMessage(text) {
    if (this.resultsEl) this.resultsEl.innerHTML = '';
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.classList.toggle('display-none', !text);
  }

  async calculate() {
    const zip = this.input.value.replace(/\D/g, '');

    if (zip.length !== 8) {
      this.showMessage(this.labels.invalid);
      return;
    }

    this.setBusy(true);
    this.showMessage('');

    let temporaryVariantId = null;

    try {
      const province = await this.resolveProvince(zip);
      temporaryVariantId = await this.ensureCartHasItem();
      const rates = await this.fetchRates(zip, province);

      if (!rates.length) {
        this.showMessage(this.labels.empty);
        return;
      }

      const selected = this.renderRates(rates);
      this.toggleClear(true);
      if (this.selectable) await this.persistRate(selected);
    } catch (error) {
      console.error('[shipping-calculator]', error);
      this.showMessage(this.labels.error);
    } finally {
      if (temporaryVariantId) await this.removeTemporaryItem(temporaryVariantId);
      this.setBusy(false);
    }
  }

  async resolveProvince(zip) {
    try {
      const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`);
      const data = await response.json();
      return data.erro ? '' : data.uf || '';
    } catch (error) {
      return '';
    }
  }

  async ensureCartHasItem() {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

    if (cart.item_count > 0) return null;

    const variantId = this.currentVariantId();
    if (!variantId) throw new Error('Nenhuma variante disponível para cotar.');

    const response = await fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    });

    if (!response.ok) throw new Error('Não foi possível montar a cotação.');

    return variantId;
  }

  async removeTemporaryItem(variantId) {
    const root = window.Shopify?.routes?.root || '/';

    try {
      await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: String(variantId), quantity: 0 })
      });
    } catch (error) {
      console.error('[shipping-calculator] falha ao remover item temporario', error);
    }
  }

  async fetchRates(zip, province) {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams();
    params.set('shipping_address[zip]', zip);
    params.set('shipping_address[country]', this.country);
    if (province) params.set('shipping_address[province]', province);

    const response = await fetch(`${root}cart/shipping_rates.json?${params}`, {
      headers: { Accept: 'application/json' }
    });

    const data = await response.json();

    if (!data.shipping_rates) throw new Error(JSON.stringify(data));

    return data.shipping_rates;
  }

  deliveryText(rate) {
    const days = rate.delivery_days || [];
    const values = days.filter((day) => day !== null && day !== undefined);

    if (!values.length) return rate.delivery_date_range_label || '';

    const min = Math.min(...values);
    const max = Math.max(...values);
    const unit = max === 1 ? this.labels.day : this.labels.days;

    return min === max ? `${min} ${unit}` : `${min} a ${max} ${unit}`;
  }

  rateCents(rate) {
    return Math.round(parseFloat(String(rate.price).replace(/[^\d.-]/g, '')) * 100) || 0;
  }

  formatPrice(price) {
    const cents = Math.round(parseFloat(String(price).replace(/[^\d.-]/g, '')) * 100) || 0;

    if (cents === 0) return 'Grátis';

    if (window.WAU?.Helpers?.formatMoney) return window.WAU.Helpers.formatMoney(cents, this.moneyFormat);

    return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }

  cheapestIndex(rates) {
    return rates.reduce((best, rate, index) => (this.rateCents(rate) < this.rateCents(rates[best]) ? index : best), 0);
  }

  renderRates(rates, selectedIndex) {
    this.showMessage('');
    if (!this.resultsEl) return 0;

    this.rates = rates;

    const selected = rates[selectedIndex] ? selectedIndex : this.cheapestIndex(rates);
    const group = `shipping-rate-${Math.random().toString(36).slice(2)}`;

    const body = `
      <span class="shipping-calculator__rate-info">
        <span class="shipping-calculator__rate-name"></span>
        <span class="shipping-calculator__rate-days"></span>
      </span>
      <span class="shipping-calculator__rate-price"></span>
    `;

    this.resultsEl.innerHTML = rates
      .map((rate, index) => {
        if (!this.selectable) return `<li><div class="shipping-calculator__rate">${body}</div></li>`;

        return `
          <li>
            <label class="shipping-calculator__rate shipping-calculator__rate--selectable">
              <input class="shipping-calculator__rate-radio" type="radio" name="${group}" value="${index}">
              ${body}
            </label>
          </li>
        `;
      })
      .join('');

    Array.from(this.resultsEl.children).forEach((item, index) => {
      const rate = rates[index];

      item.querySelector('.shipping-calculator__rate-name').textContent = rate.presentment_name || rate.name;
      item.querySelector('.shipping-calculator__rate-price').textContent = this.formatPrice(rate.price);
      item.querySelector('.shipping-calculator__rate-days').textContent = this.deliveryText(rate);

      const radio = item.querySelector('.shipping-calculator__rate-radio');
      if (!radio) return;

      radio.checked = index === selected;
      item.querySelector('.shipping-calculator__rate').classList.toggle('shipping-calculator__rate--selected', radio.checked);

      radio.addEventListener('change', () => {
        if (radio.checked) this.persistRate(index);
      });
    });

    return selected;
  }

  sanitize(text) {
    return String(text || '').replace(/\|/g, ' ').trim();
  }

  quoteValue(cents, subtotal, count, rate) {
    return [
      cents,
      subtotal,
      count,
      this.input?.value.trim() || '',
      this.sanitize(rate.presentment_name || rate.name),
      this.sanitize(this.deliveryText(rate))
    ].join('|');
  }

  writeQuote(value, sectionId) {
    const root = window.Shopify?.routes?.root || '/';
    const payload = { attributes: { _frete_estimado: value } };

    if (sectionId) payload.sections = sectionId;

    return fetch(`${root}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then((response) => response.json());
  }

  async writeQuoteAndRender(value, rate) {
    const sectionId = window.WAU?.AjaxCart?.sectionId?.();

    try {
      let state = await this.writeQuote(value, sectionId);

      if (rate) {
        const settled = this.quoteValue(this.rateCents(rate), state.items_subtotal_price, state.item_count, rate);
        if (state.attributes?._frete_estimado !== settled) {
          state = await this.writeQuote(settled, sectionId);
        }
      }

      const configEl = document.getElementById('cart-config');
      const config = configEl ? JSON.parse(configEl.innerHTML || '{}') : {};

      window.WAU?.AjaxCart?.updateView?.(config, state);
    } catch (error) {
      console.error('[shipping-calculator]', error);
    }
  }

  async persistRate(index) {
    const rate = this.rates[index];
    if (!rate) return;

    this.writeCache({
      zip: this.input?.value.replace(/\D/g, '') || '',
      rates: this.rates,
      selectedIndex: index
    });

    await this.writeQuoteAndRender(
      this.quoteValue(this.rateCents(rate), this.dataset.cartSubtotal || 0, this.dataset.cartCount || 0, rate),
      rate
    );
  }
}

if (!customElements.get('shipping-calculator')) {
  customElements.define('shipping-calculator', ShippingCalculator);
}
